import Phaser from 'phaser';

import Button from './button';
import { Winner } from './types';
import Sdk from './sdk';
import i18n from '../assets/i18n.json';

export type Grid = Array<Array<number | null>>;
export type ImageGrid = Array<Array<Phaser.GameObjects.Image | null>>;

const SCORE_BACKGROUND_COLOR = 0x778da9;
let scale = 1;

export default class ModalService {
    private scene: Phaser.Scene;
    private dialogContainer: Phaser.GameObjects.Container;
    private waterfall: Record<string, Phaser.GameObjects.Image> = {};
    
    constructor({ scene }: { scene: Phaser.Scene }) {
        const { width, height } = scene.renderer;
        this.scene = scene;
        this.dialogContainer = this.scene.add.container();
        this.scene.add.existing(this.dialogContainer);
        if (this.scene.renderer.width < 700 || this.scene.renderer.height < 700) {
            scale = 0.5;
        } else {
            scale = 1;
        }

        this.dialogContainer
            .setDepth(1)
            .setPosition(width / 2, height / 2)
            .setSize(width, height);
    }
    
    public showFinishModal(title: string, winner: Winner, score: number | null, onOk: () => void, onMenu: () => void) {
        const texts = i18n[(this.scene.registry.get('sdk') as Sdk).getLang()];

        const { width, height } = this.scene.renderer;
        this.dialogContainer.setPosition(width / 2, height / 2).setSize(width, height).setInteractive()

        const backdrop = this.scene.add.rectangle(0, 0, width, height, 0x000, 0.5)
            .setInteractive()
            .setDepth(1)
            .setName('backdrop');

        const dialogBackground = this.scene.add.graphics()
            .fillStyle(0xffffff, 1)
            .fillRoundedRect(-width*0.4, -height*0.4, width*0.8, height*0.8, 32)
            .setName('restart')
            .setName('dialogBackground');

        const text = this.scene.add.text(0, -height*0.4 + 48, title, {
            color: '#000',
            fontSize: 64 * scale,
            align: 'center',
            fontFamily: 'Trebuchet MS'
        })
            .setDepth(2)
            .setOrigin(0.5, 0)
            .setShadow(0, 0, '#808080', 0, true)
            .setName('title');

        const restartButton = new Button(this.scene, texts.restart, onOk, 'green', 'M')
            .setName('restart')
            .setPosition(0.4*width - 128 * scale, height*0.4 - 64 * scale)
            .setScale(scale);
        const menuButton = new Button(this.scene, texts.menu, onMenu, 'outline', 'M')
            .setName('menu')
            .setPosition(-0.4*width + 100 * scale, height*0.4 - 64 * scale)
            .setScale(scale);

        this.dialogContainer.add([backdrop, dialogBackground, text, restartButton, menuButton]);
        if (typeof score === 'number') {
            const scoreText = this.scene.add.text(0, text.y + 100, texts.score, {
                color: '#000',
                fontSize: 48 * scale,
                align: 'center',
                fontFamily: 'Trebuchet MS'
            }).setDepth(2).setOrigin(0.5, 0).setName('scoreText');

            const scoreNumber = this.scene.add.text(0, -this.dialogContainer.height / 20, String(score), {
                color: '#FFF',
                fontSize: height/10,
                align: 'center',
                fontFamily: 'Trebuchet MS'
            })
                .setDepth(2)
                .setOrigin(0.5, 0)
                .setName('scoreNumber');
            const scoreBackground = this.scene.add.graphics()
                .fillStyle(SCORE_BACKGROUND_COLOR)
                .fillRoundedRect(scoreNumber.x-scoreNumber.width, scoreNumber.y-scoreNumber.height/2, scoreNumber.width*2, scoreNumber.height*2, Math.min(width, height)/24)
                .setName('scoreBackground');

            if (this.scene.renderer.height < 768) {
                scoreNumber.setFontSize(Math.min(width, height)/12);
                scoreText.setFontSize(width/24);
                scoreNumber.x = scoreText?.x + scoreText.width*scoreText.originX + scoreNumber.width * 1.1;
                scoreNumber.y = scoreText?.y;
                scoreBackground
                    .clear()
                    .fillStyle(SCORE_BACKGROUND_COLOR)
                    .fillRoundedRect(scoreNumber.x-scoreNumber.width, scoreNumber.y-scoreNumber.height/2, scoreNumber.width*2, scoreNumber.height*2, 32);
            }
            
            this.dialogContainer.add([scoreText, scoreBackground, scoreNumber]);
        }

        this.startWaterfall(winner);
    }

    private startWaterfall(type: Winner) {
        const count = 100;

        for (let index = 0; index < count; index++) {
            const id = String(Math.random());
            this.waterfall[id] = this.scene.add.image((Math.random() - 0.5) * this.scene.renderer.width, -0.5 * this.scene.renderer.height - Math.random()*500 - 100, type)
                .setName(id)
                .setScale(0.2 + Math.random());
            this.dialogContainer.add(this.waterfall[id]);
            this.scene.tweens.add({
                targets: this.waterfall[id],
                y: '+='+(this.scene.renderer.height+600),
                duration: 5000 + Math.random() * 3000,
                loop: 20,
                delay: 8000 * Math.random()
            });
        }
    }

    public close() {
        this.dialogContainer.removeAllListeners().removeAll(true).removeInteractive();
    }

    public afterResize(scale: number) {
        let { width, height } = this.scene.renderer;
        this.dialogContainer.setPosition(width / 2, height / 2);
        (this.dialogContainer.getByName('backdrop') as Phaser.GameObjects.Rectangle)?.setSize(width, height);
        (this.dialogContainer.getByName('dialogBackground') as Phaser.GameObjects.Graphics)
            ?.clear()
            .fillStyle(0xffffff, 1)
            .fillRoundedRect(-width*0.4, -height*0.4, width*0.8, height*0.8, 32);
        (this.dialogContainer.getByName('restart') as Button)?.setPosition(0.4*width - 128 * scale, height*0.4 - 64 * scale).setScale(scale);
        (this.dialogContainer.getByName('menu') as Button)?.setPosition(-0.4*width + 100 * scale, height*0.4 - 64 * scale).setScale(scale);
        const text = (this.dialogContainer.getByName('title') as Phaser.GameObjects.Text)?.setY(-height*0.4 + 48);
        const scoreText = (this.dialogContainer.getByName('scoreText') as Phaser.GameObjects.Text)?.setPosition(0, text.y + 100);
        
        const scoreNumber = (this.dialogContainer.getByName('scoreNumber') as Phaser.GameObjects.Text)?.setPosition(0, -this.dialogContainer.height / 13).setFontSize(height/10);
        const scoreBackground = (this.dialogContainer.getByName('scoreBackground') as Phaser.GameObjects.Graphics)
            ?.clear()
            .fillStyle(SCORE_BACKGROUND_COLOR, 1)
            .fillRoundedRect(scoreNumber.x-scoreNumber.width, scoreNumber.y-scoreNumber.height/2, scoreNumber.width*2, scoreNumber.height*2, Math.min(width, height)/24);
        
        if (height < 768) {
            text?.setFontSize(Math.min(width, height)/12 * scale);
            scoreText?.setFontSize(width/24 * scale);
            if (scoreNumber) {
                scoreNumber?.setFontSize(Math.min(width, height)/12 * scale);
                scoreNumber.x = scoreText?.x + scoreText?.width*scoreText?.originX + scoreNumber?.width * 1.1;
                scoreNumber.y = scoreText?.y;
            }
            scoreBackground
                ?.clear()
                .fillStyle(SCORE_BACKGROUND_COLOR, 1)
                .fillRoundedRect(scoreNumber.x-scoreNumber.width, scoreNumber.y-scoreNumber.height/2, scoreNumber.width*2, scoreNumber.height*2, Math.min(width, height)/24);
        }
    }
}